<template>
  <div class="single-level-selector" :class="wrapperClasses">
    <div class="selector-trigger" @click.stop="togglePanel">
      <div class="selected-text">
        <span class="summary">{{ displayText }}</span>
      </div>
      <i class="arrow" :class="{ up: isPanelOpen }">
        <img src="@/assets/icon_common_special_arrow_9.png" alt="arrow" />
      </i>
    </div>

    <transition name="selector-fade">
      <div class="selector-panel" v-if="isPanelOpen" @click.stop>
        <div class="panel-body">
          <div class="option all-option" @click.stop="handleToggleAll">
            <div class="checkbox-img">
              <img v-if="isTempAllChecked" :src="checkboxIcons.checked" alt="已全选" />
              <img v-else-if="isTempAllIndeterminate" :src="checkboxIcons.indeterminate" alt="部分选中" />
              <img v-else :src="checkboxIcons.unchecked" alt="未选" />
            </div>
            <span>{{ allText }}</span>
          </div>

          <div
            class="option"
            v-for="opt in normalizedOptions"
            :key="opt.value"
            @click.stop="toggleOption(opt.value)"
          >
            <div class="checkbox-img">
              <img v-if="isTempChecked(opt.value)" :src="checkboxIcons.checked" alt="选中" />
              <img v-else :src="checkboxIcons.unchecked" alt="未选" />
            </div>
            <span class="label">{{ opt.label }}</span>
          </div>
        </div>

        <div class="panel-footer">
          <button class="btn cancel" @click="handleCancel">取消</button>
          <button class="btn confirm" @click="handleConfirm" :style="confirmButtonStyle">确定</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import checkedIcon from '@/assets/img/CascadeSelector/allSel.png';
import indeterminateIcon from '@/assets/img/CascadeSelector/mulSel.png';
import uncheckedIcon from '@/assets/img/CascadeSelector/noSel.png';

export default {
  name: 'SingleLevelSelector',
  props: {
    options: {
      type: Array,
      default: () => []
    },
    value: {
      type: Array,
      default: () => []
    },
    labelKey: {
      type: String,
      default: 'label'
    },
    valueKey: {
      type: String,
      default: 'value'
    },
    allText: {
      type: String,
      default: '全部'
    },
    emptyText: {
      type: String,
      default: '未选中'
    },
    unitText: {
      type: String,
      default: '项'
    },
    maxPreviewLength: {
      type: Number,
      default: 8
    },
    classType: {
      type: String,
      default: ''
    },
    checkboxIcons: {
      type: Object,
      default: () => ({
        checked: checkedIcon,
        indeterminate: indeterminateIcon,
        unchecked: uncheckedIcon
      })
    },
    confirmButtonStyle: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      isPanelOpen: false,
      tempSelectedValues: []
    };
  },
  computed: {
    normalizedOptions() {
      return this.options.map(item => ({
        label: item[this.labelKey],
        value: item[this.valueKey]
      }));
    },
    optionLabelMap() {
      const map = new Map();
      this.normalizedOptions.forEach(opt => {
        map.set(opt.value, opt.label);
      });
      return map;
    },
    selectedValues() {
      return Array.isArray(this.value) ? this.value : [];
    },
    selectedLabels() {
      return this.selectedValues
        .map(val => this.optionLabelMap.get(val))
        .filter(Boolean);
    },
    isAllSelected() {
      return (
        this.normalizedOptions.length > 0 &&
        this.selectedValues.length === this.normalizedOptions.length
      );
    },
    displayText() {
      if (this.isAllSelected || this.normalizedOptions.length === 0) {
        return this.allText;
      }
      if (this.selectedValues.length === 0) {
        return this.emptyText;
      }
      if (this.selectedValues.length === 1) {
        return this.selectedLabels[0] || this.emptyText;
      }

      const firstLabel = this.selectedLabels[0] || '';
      const shouldEllipsis = firstLabel.length > this.maxPreviewLength;
      const truncated = shouldEllipsis
        ? `${firstLabel.slice(0, this.maxPreviewLength)}...`
        : `${firstLabel}${firstLabel.endsWith('...') ? '' : '...'}`;
      return `${truncated}等${this.selectedValues.length}${this.unitText}`;
    },
    isTempAllChecked() {
      return (
        this.normalizedOptions.length > 0 &&
        this.tempSelectedValues.length === this.normalizedOptions.length
      );
    },
    isTempAllIndeterminate() {
      return (
        this.tempSelectedValues.length > 0 &&
        this.tempSelectedValues.length < this.normalizedOptions.length
      );
    },
    wrapperClasses() {
      return [
        this.classType ? `${this.classType}-single-selector` : '',
        { open: this.isPanelOpen }
      ];
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.tempSelectedValues = Array.isArray(val) ? [...val] : [];
      }
    }
  },
  methods: {
    togglePanel() {
      this.isPanelOpen ? this.closePanel() : this.openPanel();
    },
    openPanel() {
      this.isPanelOpen = true;
      this.tempSelectedValues = [...this.selectedValues];
      document.addEventListener('click', this.handleOutsideClick);
    },
    closePanel() {
      this.isPanelOpen = false;
      this.tempSelectedValues = [...this.selectedValues];
      document.removeEventListener('click', this.handleOutsideClick);
    },
    handleOutsideClick(event) {
      if (!this.$el.contains(event.target)) {
        this.closePanel();
      }
    },
    toggleOption(value) {
      const idx = this.tempSelectedValues.indexOf(value);
      if (idx > -1) {
        this.tempSelectedValues.splice(idx, 1);
      } else {
        this.tempSelectedValues.push(value);
      }
    },
    isTempChecked(value) {
      return this.tempSelectedValues.includes(value);
    },
    handleToggleAll() {
      if (this.isTempAllChecked) {
        this.tempSelectedValues = [];
      } else {
        this.tempSelectedValues = this.normalizedOptions.map(opt => opt.value);
      }
    },
    buildPayload() {
      return {
        values: [...this.tempSelectedValues],
        labels: this.tempSelectedValues
          .map(val => this.optionLabelMap.get(val))
          .filter(Boolean),
        isSelectAll:
          this.normalizedOptions.length > 0 &&
          this.tempSelectedValues.length === this.normalizedOptions.length
      };
    },
    handleConfirm() {
      const payload = this.buildPayload();
      this.$emit('confirm', payload);
      this.$emit('input', payload.values);
      this.closePanel();
    },
    handleCancel() {
      this.closePanel();
      this.$emit('cancel');
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
  }
};
</script>

<style lang="scss" scoped>
.single-level-selector {
  position: relative;
  display: inline-block;

  .selector-trigger {
    display: inline-flex;
    align-items: center;
    height: 40px;
    padding: 0 16px;
    background: rgba(74, 124, 232, 0.1);
    border-radius: 80px;
    border: 1px solid rgba(74, 124, 232, 0.3);
    cursor: pointer;
    transition: border 0.2s ease;

    .selected-text {
      display: block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      .summary {
        font-size: 14px;
        color: #4a7ce8;
      }
    }

    .arrow {
      margin-left: 8px;
      transition: transform 0.2s ease;
      img {
        width: 14px;
        height: 14px;
      }
      &.up {
        transform: rotate(180deg);
      }
    }
  }

  .selector-panel {
    position: absolute;
    top: calc(100% + 6px);
    width: 212px;
    background: #fff;
    box-shadow: 0px 0px 10px 0px rgba(94, 103, 121, 0.2);
    border-radius: 8px;
    border: none;
    z-index: 20;
    display: flex;
    flex-direction: column;

    .panel-body {
      max-height: 360px;
      overflow: auto;
      padding: 4px 4px 4px 4px;
    }
		.panel-body::-webkit-scrollbar {
			display: none;
		}

    .option {
      display: flex;
      align-items: center;
      padding: 10px 8px;
      border-radius: 2px;
      cursor: pointer;
      transition: background 0.2s ease;

      &:hover {
        background: rgba(74, 124, 232, 0.1);
      }

      .checkbox-img {
        width: 14px;
        height: 14px;
        margin-right: 8px;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
          width: 14px;
          height: 14px;
        }
      }

      .label {
        flex: 1;
        font-size: 14px;
        color: #4b5563;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      &.all-option {
        font-weight: 400;
        color: #4b5563;
      }
    }

    .panel-footer {
      display: flex;
      justify-content: flex-end;
      padding: 10px 12px 12px 12px;
      gap: 8px;
			border-top: 1px solid #f0f0f0;

      .btn {
        min-width: 60px;
        height: 32px;
        border-radius: 16px;
        border: none;
        cursor: pointer;
        border-radius: 2px;
				border: 1px solid rgba(0, 0, 0, 0.15);
				font-family: PingFangSC, PingFang SC;
				font-weight: 400;
				font-size: 14px;
				color: rgba(0, 0, 0, 0.65);
				line-height: 12px;
      }

      .cancel {
        background: #ffffff00;
        color: rgba(0, 0, 0, 0.65);
      }

      .confirm {
        background: #1890ff;
        color: #fff;
				border: unset;
      }
    }
  }
}

.selector-fade-enter-active,
.selector-fade-leave-active {
  transition: all 0.2s ease;
}

.selector-fade-enter,
.selector-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
